import React, { useState } from 'react';
import Layout from '../components/layout/Layout';
import hero from "../assets/certificatehero.jpg";
import { FileText, ExternalLink, X, ChevronLeft, ChevronRight } from 'lucide-react';
import CTASection from "../components/common/CTASection";

// Import certificate images
import Cert1 from '../assets/certificate1.jpg';
import Cert2 from '../assets/certificate2.jpg';
import Cert3 from '../assets/certificate3.jpg';
import Cert4 from '../assets/certificate4.jpg';
import Cert5 from '../assets/certificate5.jpg';
import Cert6 from '../assets/certificate6.jpg';
import Cert7 from '../assets/certificate7.jpg';
import Cert8 from '../assets/certificate8.jpg';

// Import PDFs from assets/certificate folder
import Pdf1 from '../assets/certificate/iso-9001-2015.pdf';
import Pdf2 from '../assets/certificate/iso-14001-2015.pdf';
import Pdf3 from '../assets/certificate/iso-22000-2018.pdf';
import Pdf4 from '../assets/certificate/iso-45001-2018.pdf';
import Pdf5 from '../assets/certificate/haccp-certificate.pdf';
import Pdf6 from '../assets/certificate/environment-policy.pdf';
import Pdf7 from '../assets/certificate/health-safety-policy.pdf';
import Pdf8 from '../assets/certificate/quality-policy.pdf';

const CertificatesPage = () => {
  const [selectedImage, setSelectedImage] = useState(null);
  const [currentIndex, setCurrentIndex] = useState(0);

  const certificates = [
    {
      id: 1,
      title: "ISO 9001:2015",
      description: "Quality Management Systems Certificate",
      image: Cert1,
      pdfUrl: Pdf1
    },
    {
      id: 2,
      title: "ISO 14001:2015",
      description: "Environmental Management Systems",
      image: Cert2,
      pdfUrl: Pdf2
    },
    {
      id: 3,
      title: "ISO 22000:2018",
      description: "Food Safety Management Systems",
      image: Cert3,
      pdfUrl: Pdf3
    },
    {
      id: 4,
      title: "ISO 45001:2018",
      description: "Occupational Health and Safety Management",
      image: Cert4,
      pdfUrl: Pdf4
    },
    {
      id: 5,
      title: "HACCP Certificate",
      description: "Hazard Analysis and Critical Control Points",
      image: Cert5,
      pdfUrl: Pdf5
    },
    {
      id: 6,
      title: "Environment Policy",
      description: "Commitment to Environmental Excellence",
      image: Cert6,
      pdfUrl: Pdf6
    },
    {
      id: 7,
      title: "Health & Safety Policy",
      description: "Commitment to Workplace Safety",
      image: Cert7,
      pdfUrl: Pdf7
    },
    {
      id: 8,
      title: "Quality Policy",
      description: "Commitment to Quality Excellence",
      image: Cert8,
      pdfUrl: Pdf8
    }
  ];

  const openPDF = (pdfUrl) => {
    window.open(pdfUrl, '_blank');
  };

  const openLightbox = (index) => {
    setCurrentIndex(index);
    setSelectedImage(certificates[index]);
  };

  const closeLightbox = () => {
    setSelectedImage(null);
  };

  const nextImage = () => {
    const newIndex = (currentIndex + 1) % certificates.length;
    setCurrentIndex(newIndex);
    setSelectedImage(certificates[newIndex]);
  };

  const prevImage = () => {
    const newIndex = (currentIndex - 1 + certificates.length) % certificates.length;
    setCurrentIndex(newIndex);
    setSelectedImage(certificates[newIndex]);
  };

  return (
    <Layout>
      {/* ===== HERO SECTION ===== */}
      <section 
        className="relative py-16 sm:py-20 bg-fixed bg-cover bg-center"
        style={{ 
          backgroundImage: `url(${hero})`,
          backgroundAttachment: "fixed"
        }}
      >
        {/* Dark Overlay */}
        <div className="absolute inset-0 bg-[#2F3C7E]/70"></div>
        
        {/* Content */}
        <div className="container mx-auto px-4 text-center relative z-10">
          <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold text-white mb-3 sm:mb-4">
            Our Certificates
          </h1>
          <p className="text-lg sm:text-xl text-[#E6B325]">
            Recognized for Excellence & Quality
          </p>
        </div>
      </section>

      {/* ===== CERTIFICATES SECTION ===== */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 max-w-7xl mx-auto">
            {certificates.map((cert, index) => (
              <div 
                key={cert.id}
                className="group bg-white rounded-xl shadow-md hover:shadow-xl transition overflow-hidden border border-gray-100"
              >
                {/* Certificate Image - Click opens PDF directly */}
                <div 
                  className="relative overflow-hidden cursor-pointer"
                  onClick={() => openPDF(cert.pdfUrl)}
                >
                  <img 
                    src={cert.image} 
                    alt={cert.title}
                    className="w-full h-48 sm:h-52 md:h-56 object-cover transition duration-500 group-hover:scale-110"
                  />
                  <div className="absolute inset-0 bg-[#2F3C7E]/80 opacity-0 group-hover:opacity-100 transition duration-300 flex items-center justify-center">
                    <div className="bg-white rounded-full p-2.5 sm:p-3 transform scale-0 group-hover:scale-100 transition duration-300">
                      <ExternalLink className="w-5 h-5 sm:w-6 sm:h-6 text-[#2F3C7E]" />
                    </div>
                  </div>
                </div>
                
                {/* Certificate Info */}
                <div className="p-4 sm:p-5">
                  <h3 className="text-base sm:text-lg font-bold text-[#2F3C7E] mb-1">
                    {cert.title}
                  </h3>
                  <p className="text-xs sm:text-sm text-gray-600 mb-3">
                    {cert.description}
                  </p>
                  <button
                    onClick={() => openPDF(cert.pdfUrl)}
                    className="inline-flex items-center gap-2 text-[#E6B325] hover:text-[#c99a1a] font-semibold text-xs sm:text-sm transition"
                  >
                    View PDF
                    <ExternalLink className="w-3 h-3 sm:w-4 sm:h-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <CTASection />

      {/* ===== LIGHTBOX MODAL - Now just for image preview, PDF on button click ===== */}
      {selectedImage && (
        <div 
          className="fixed inset-0 bg-black/95 z-50 flex items-center justify-center p-4"
          onClick={closeLightbox}
        >
          {/* Close button */}
          <button 
            onClick={closeLightbox}
            className="absolute top-4 right-4 md:top-8 md:right-8 text-white bg-black/60 hover:bg-black/80 rounded-full p-2 sm:p-3 transition z-20"
          >
            <X className="w-5 h-5 sm:w-6 sm:h-6 md:w-7 md:h-7" />
          </button>

          {/* Left navigation button */}
          <button 
            onClick={(e) => {
              e.stopPropagation();
              prevImage();
            }}
            className="absolute left-4 md:left-8 top-1/2 transform -translate-y-1/2 text-white bg-black/60 hover:bg-black/80 rounded-full p-2 sm:p-3 transition z-20"
          >
            <ChevronLeft className="w-5 h-5 sm:w-6 sm:h-6 md:w-7 md:h-7" />
          </button>

          {/* Right navigation button */}
          <button 
            onClick={(e) => {
              e.stopPropagation();
              nextImage();
            }}
            className="absolute right-4 md:right-8 top-1/2 transform -translate-y-1/2 text-white bg-black/60 hover:bg-black/80 rounded-full p-2 sm:p-3 transition z-20"
          >
            <ChevronRight className="w-5 h-5 sm:w-6 sm:h-6 md:w-7 md:h-7" />
          </button>

          {/* Image Container */}
          <div 
            className="relative max-w-5xl w-full max-h-[80vh] flex flex-col items-center justify-center"
            onClick={(e) => e.stopPropagation()}
          >
            <img 
              src={selectedImage.image} 
              alt={selectedImage.title}
              className="max-w-full max-h-[70vh] object-contain rounded-lg shadow-2xl"
            />
            
            {/* Info and PDF button */}
            <div className="mt-6 flex flex-col sm:flex-row items-center gap-3 sm:gap-6">
              <div className="bg-black/60 rounded-full px-4 py-2 text-white text-xs sm:text-sm">
                {currentIndex + 1} / {certificates.length}
              </div>
              <button
                onClick={() => openPDF(selectedImage.pdfUrl)}
                className="bg-[#E6B325] hover:bg-[#c99a1a] text-[#2F3C7E] px-5 sm:px-6 py-2 rounded-full font-semibold flex items-center gap-2 transition text-sm"
              >
                <ExternalLink className="w-4 h-4" />
                Open PDF
              </button>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
};

export default CertificatesPage;